import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { authApi, ABS_BASE } from '../lib/api';
import LoadingOverlay from '../components/ui/LoadingOverlay';

interface EmssUser {
  id: string;
  email: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  preferred_language: string | null;
  current_ers: number;
  discord_id: string | null;
  discord_verified: boolean;
  steam_id: string | null;
  steam_verified: boolean;
  xbox_id: string | null;
  xbox_gamertag: string | null;
  xbox_verified: boolean;
  /** Sugestão de gamertag detectada em telemetria autenticada (aguarda confirmação). */
  xbox_gamertag_suggested: string | null;
  xbox_gamertag_suggested_at: string | null;
  country_code: string | null;
  priority_game_id: string | null;
  is_active: boolean;
  privacy_policy_version: string | null;
  privacy_policy_accepted_at: string | null;
  roles: string[];
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  emssUser: EmssUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  linkDiscord: () => void;
  unlinkDiscord: () => Promise<void>;
  linkSteam: () => void;
  unlinkSteam: () => Promise<void>;
  linkXbox: (gamertag: string) => Promise<any>;
  unlinkXbox: () => Promise<void>;
  refreshEmssUser: () => Promise<void>;
  updateProfile: (data: { 
    display_name?: string; 
    avatar_url?: string;
    bio?: string;
    location?: string;
    country_code?: string;
    preferred_language?: string;
    priority_game_id?: string;
    privacy_policy_version?: string;
    privacy_policy_accepted_at?: string;
    is_active?: boolean;
  }) => Promise<void>;
  onlineCount: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Otimização de Egress: intervalo mínimo entre refreshes de perfil disparados
// por eventos de background (visibilitychange/online). Em mobile/PWA esses
// eventos disparam com frequência e cada chamada gera 2 queries ao Supabase.
const PROFILE_REFRESH_MIN_INTERVAL_MS = 60_000;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session,  setSession]  = useState<Session | null>(null);
  const [user,     setUser]     = useState<User | null>(null);
  const [emssUser, setEmssUser] = useState<EmssUser | null>(null);
  const [loading,  setLoading]  = useState(true);
  // authActionLoading: overlay apenas durante ações explícitas de auth (login, logout, link/unlink)
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const [onlineCount, setOnlineCount] = useState(1);
  const isRefreshing = useRef(false);
  // Otimização de Egress: throttle de refresh de perfil em background
  // (visibilitychange/online). Ver throttledRefresh().
  const lastProfileRefresh = useRef(0);

  // Loading de ações de auth (login, logout, link/unlink) — não rastrea chamadas de background
  // O onLoadingChange foi mantido por compatibilidade mas não aciona mais overlay

  /** Recarrega dados do perfil (rating, etc.) e verifica sessão via Cookie.
   *  Faz até 5 tentativas com backoff exponencial para lidar com 503 (cold start). */
  const refreshEmssUser = async () => {
    if (isRefreshing.current) return;
    isRefreshing.current = true;

    const MAX_RETRIES = 5;
    const BASE_DELAY = 1500; // ms

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const { usersApi } = await import('../lib/api');
        const data = await usersApi.me() as { user: EmssUser };

        setEmssUser(data.user);

        // Sincroniza o "user" do Supabase com o que o backend retornou.
        // Isso é vital pois o ProtectedRoute e outros componentes dependem de "user".
        // Como usamos Cookies HttpOnly, o cliente JS do Supabase pode não ter a sessão,
        // então nós a "emulamos" aqui a partir do sucesso do me().
        setUser(prev => ({
          ...(prev || {}),
          id: data.user.id,
          email: data.user.email || prev?.email,
          user_metadata: {
            ...(prev?.user_metadata || {}),
            full_name: prev?.user_metadata?.full_name || data.user.display_name,
            avatar_url: data.user.avatar_url || prev?.user_metadata?.avatar_url
          }
        } as User));

        isRefreshing.current = false;
        setLoading(false);
        return data.user;

      } catch (err: any) {
        const status = err?.status ?? 0;
        const isRetryable = (status === 503 || status === 0 || err?.name === 'TypeError') && attempt < MAX_RETRIES;

        if (isRetryable) {
          const delay = Math.min(BASE_DELAY * (attempt + 1), 8000);
          console.warn(`[AuthContext] 503 no /users/me — aguardando ${delay}ms (tentativa ${attempt + 1}/${MAX_RETRIES})`);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }

        // 401 / 403 → usuário não autenticado (não é erro do servidor)
        if (status !== 401 && status !== 403) {
          console.error('[AuthContext] Falha ao carregar usuário e-MSS:', err);
        }
        setEmssUser(null);
        setUser(null);
        isRefreshing.current = false;
        setLoading(false);
        return null;
      }
    }

    // Esgotou retries
    console.error('[AuthContext] Servidor não respondeu após múltiplas tentativas.');
    setEmssUser(null);
    setUser(null);
    isRefreshing.current = false;
    setLoading(false);
    return null;
  };

  useEffect(() => {
    // 1. Escuta mudanças de auth do Supabase (para metadados locais se existirem)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        setSession(session);
        setUser(session.user);
        refreshEmssUser();
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setEmssUser(null);
        setLoading(false);
      }
    });

    // 2. Verifica se há erros de login retornados pelo Backend no redirecionamento da página de login
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('error');
    if (authError && window.location.pathname.startsWith('/login')) {
      console.error('[AuthContext] Erro de autenticação detectado na URL:', authError);
      alert(`Falha no Login: ${authError}. Verifique os logs do backend.`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // 3. Inicialização via Backend (Fonte de verdade para Cookies HttpOnly)
    refreshEmssUser();

    return () => subscription.unsubscribe();
  }, []);

  // 4. Presence - Contador de Usuários Online
  // Requer Realtime habilitado no Supabase. Falha graciosamente se não estiver configurado.
  useEffect(() => {
    if (!emssUser?.id) return;

    let channel: any = null;
    try {
      channel = supabase.channel('online-presence', {
        config: { presence: { key: emssUser.id } },
      });

      channel
        .on('presence', { event: 'sync' }, () => {
          try {
            const state = channel.presenceState();
            setOnlineCount(Object.keys(state).length);
          } catch { /* ignore */ }
        })
        .subscribe(async (status: string) => {
          if (status === 'SUBSCRIBED') {
            try {
              await channel.track({
                user_id: emssUser.id,
                online_at: new Date().toISOString(),
              });
            } catch { /* Realtime não configurado — ignora silenciosamente */ }
          }
        });
    } catch (e) {
      console.warn('[AuthContext] Realtime Presence indisponível:', e);
    }

    // Reconexão mobile: re-registra presence ao voltar do background ou recuperar conexão
    // Otimização de Egress: throttle de 60s nos refreshes de perfil — em mobile/PWA
    // `visibilitychange` dispara com frequência e cada chamada gera 2 queries ao Supabase.
    const throttledRefresh = async () => {
      const now = Date.now();
      if (now - lastProfileRefresh.current < PROFILE_REFRESH_MIN_INTERVAL_MS) return;
      lastProfileRefresh.current = now;
      await refreshEmssUser();
    };
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        // Re-registra presence caso o canal tenha sido suspenso pelo mobile
        if (channel) {
          const state = channel.presenceState?.() ?? {};
          const alreadyTracked = Object.keys(state).length > 0;
          if (!alreadyTracked) {
            await channel.track({
              user_id: emssUser.id,
              online_at: new Date().toISOString(),
            });
          }
        }
        // Atualiza dados do perfil em background (throttled)
        await throttledRefresh();
      } catch { /* ignora */ }
    };

    const handleOnline = async () => {
      // Ao recuperar Wi-Fi/dados, garante que o perfil está atualizado (throttled)
      try {
        await throttledRefresh();
        if (channel) {
          await channel.track({
            user_id: emssUser.id,
            online_at: new Date().toISOString(),
          });
        }
      } catch { /* ignora */ }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      try { channel?.unsubscribe(); } catch { /* ignore */ }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [emssUser?.id]);
  
  // 5. Sincronização de Idioma com o Perfil
  useEffect(() => {
    const syncLang = async () => {
      if (emssUser?.preferred_language) {
        const { default: i18nInstance } = await import('../i18n');
        if (emssUser.preferred_language !== i18nInstance.language) {

          i18nInstance.changeLanguage(emssUser.preferred_language);
        }
      }
    };
    syncLang();
  }, [emssUser?.preferred_language]);

  const signInWithGoogle = async () => {
    // Fluxo SEGURO: Vai para o Backend, que cuida do OAuth e volta com Cookies HttpOnly.
    setAuthActionLoading(true);
    const targetUrl = `${ABS_BASE}/auth/google`;
    console.log('[Auth] Redirecionando para:', targetUrl);
    window.location.href = targetUrl;
  };

  const linkDiscord = () => {
    window.location.href = `${ABS_BASE}/auth/discord`;
  };

  const unlinkDiscord = async () => {
    try {
      setAuthActionLoading(true);
      await authApi.discordUnlink();
      await refreshEmssUser();
    } finally {
      setAuthActionLoading(false);
    }
  };

  const linkSteam = () => {
    window.location.href = `${ABS_BASE}/auth/steam`;
  };

  const unlinkSteam = async () => {
    try {
      setAuthActionLoading(true);
      await authApi.steamUnlink();
      await refreshEmssUser();
    } finally {
      setAuthActionLoading(false);
    }
  };

  const linkXbox = async (gamertag: string) => {
    try {
      setAuthActionLoading(true);
      const res = await authApi.xboxLink(gamertag);
      await refreshEmssUser();
      return res;
    } finally {
      setAuthActionLoading(false);
    }
  };

  const unlinkXbox = async () => {
    try {
      setAuthActionLoading(true);
      await authApi.xboxUnlink();
      await refreshEmssUser();
    } finally {
      setAuthActionLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setAuthActionLoading(true);
      await authApi.signOut();
      setEmssUser(null);
      setUser(null);
      setSession(null);
      await supabase.auth.signOut();
    } finally {
      setAuthActionLoading(false);
      window.location.href = '/';
    }
  };

  const updateProfile = async (data: { 
    display_name?: string; 
    avatar_url?: string;
    bio?: string;
    location?: string;
    country_code?: string;
    preferred_language?: string;
    priority_game_id?: string;
    privacy_policy_version?: string;
    privacy_policy_accepted_at?: string;
    is_active?: boolean;
  }) => {
    // Atualização otimista: aplica o estado localmente antes da resposta do servidor
    const previous = emssUser;
    if (emssUser) {
      setEmssUser({ ...emssUser, ...data } as EmssUser);
    }
    try {
      const { usersApi } = await import('../lib/api');
      await usersApi.updateMe(data);
      // Sincroniza com o servidor em background (sem overlay)
      await refreshEmssUser();
    } catch (err: any) {
      // Rollback em caso de falha
      setEmssUser(previous);
      console.error('[AuthContext] Erro em updateProfile:', err?.message || err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      session, user, emssUser, loading,
      signOut, signInWithGoogle, linkDiscord, unlinkDiscord, linkSteam, unlinkSteam, linkXbox, unlinkXbox, refreshEmssUser,
      updateProfile, onlineCount
    }}>
      {/* Overlay apenas durante o carregamento inicial de auth e ações explícitas (login, logout, link) */}
      <LoadingOverlay isVisible={loading || authActionLoading} />
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
